import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { userApi, roleApi, businessApi, loyverseApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Users as UsersIcon, Shield, Store, Mail } from 'lucide-react';
import MultiSelectFilter from '../components/MultiSelectFilter';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Users = () => {
  const { user: currentUser, token } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [allBranches, setAllBranches] = useState([]); // All branches across businesses
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const [welcomeUser, setWelcomeUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(null); // Track which user is receiving welcome email

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role_id: '',
    business_ids: [],
    branch_ids: [],
  });

  const openWelcomeDialog = (user) => {
    setWelcomeUser(user);
    setWelcomeDialogOpen(true);
  };

  const sendWelcomeEmail = async () => {
    if (!welcomeUser) return;
    
    setSendingWelcome(welcomeUser.id);
    setWelcomeDialogOpen(false);
    
    try {
      await axios.post(
        `${API_URL}/api/account/send-welcome/${welcomeUser.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Welcome email sent successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send welcome email';
      toast.error(message);
    } finally {
      setSendingWelcome(null);
      setWelcomeUser(null);
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, businessesRes] = await Promise.all([
        userApi.getAll(),
        roleApi.getAll(),
        businessApi.getAll(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setBusinesses(businessesRes.data);
      
      // Fetch branches for all businesses
      const branchPromises = businessesRes.data.map(async (biz) => {
        try {
          const res = await loyverseApi.getBranches(biz.id);
          return (res.data || []).map(b => ({ ...b, business_name: biz.name }));
        } catch {
          return [];
        }
      });
      const branchResults = await Promise.all(branchPromises);
      setAllBranches(branchResults.flat());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        password: '',
        name: user.name,
        role_id: user.role_id,
        business_ids: user.business_ids || [],
        branch_ids: user.branch_ids || [],
      });
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        password: '',
        name: '',
        role_id: '',
        business_ids: [],
        branch_ids: [],
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.role_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      if (selectedUser) {
        const updateData = {
          name: formData.name,
          role_id: formData.role_id,
          business_ids: formData.business_ids,
          branch_ids: formData.branch_ids,
        };
        // Include password only if provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        await userApi.update(selectedUser.id, updateData);
        toast.success('User updated successfully');
      } else {
        await userApi.create(formData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await userApi.delete(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role?.name?.replace('_', ' ') || 'Unknown';
  };

  const getBusinessNames = (businessIds) => {
    return businesses
      .filter((b) => businessIds?.includes(b.id))
      .map((b) => b.name)
      .join(', ');
  };

  const getBranchNames = (branchIds) => {
    if (!branchIds || branchIds.length === 0) return 'All Branches';
    return allBranches
      .filter((b) => branchIds.includes(b.loyverse_store_id))
      .map((b) => b.name)
      .join(', ') || 'All Branches';
  };

  // Get branches for selected businesses only
  const availableBranches = allBranches.filter((branch) => {
    const business = businesses.find(b => b.id === branch.business_id);
    return formData.business_ids.includes(branch.business_id) || 
           formData.business_ids.some(bid => businesses.find(b => b.id === bid));
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-coffee-espresso">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users and their access permissions
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn" className="rounded-xl">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Businesses</TableHead>
                  <TableHead>Branches</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize rounded-full">
                          {getRoleName(user.role_id)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-36 truncate">
                        {getBusinessNames(user.business_ids) || '-'}
                      </TableCell>
                      <TableCell className="max-w-36 truncate">
                        <span className="text-sm">
                          {getBranchNames(user.branch_ids)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={user.is_active ? 'default' : 'secondary'}
                          className={user.is_active ? 'bg-green-100 text-green-700' : ''}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openWelcomeDialog(user)}
                            disabled={sendingWelcome === user.id}
                            title="Send welcome email"
                            data-testid={`send-welcome-${user.id}`}
                          >
                            {sendingWelcome === user.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(user)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {selectedUser ? 'Update user details and permissions' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                data-testid="user-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                disabled={!!selectedUser}
                data-testid="user-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{selectedUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={selectedUser ? "Enter new password" : "Enter password"}
                data-testid="user-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select
                value={formData.role_id}
                onValueChange={(value) => setFormData({ ...formData, role_id: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        <span className="capitalize">{role.name.replace('_', ' ')}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <MultiSelectFilter
              label="Assigned Businesses"
              options={businesses.map((b) => ({ id: b.id, name: b.name }))}
              selected={formData.business_ids}
              onChange={(ids) => {
                setFormData({ ...formData, business_ids: ids, branch_ids: [] });
                // Fetch branches for newly selected businesses
                ids.forEach(async (bizId) => {
                  if (!allBranches.some(b => b.business_id === bizId)) {
                    try {
                      const res = await loyverseApi.getBranches(bizId);
                      const bizName = businesses.find(b => b.id === bizId)?.name || '';
                      const newBranches = (res.data || []).map(b => ({ ...b, business_name: bizName }));
                      setAllBranches(prev => [...prev.filter(b => b.business_id !== bizId), ...newBranches]);
                    } catch (err) {
                      console.error('Failed to fetch branches:', err);
                    }
                  }
                });
              }}
              testId="user-businesses-select"
            />

            {formData.business_ids.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-muted-foreground" />
                  <Label>Branch Access</Label>
                </div>
                {allBranches.filter(b => formData.business_ids.includes(b.business_id)).length > 0 ? (
                  <MultiSelectFilter
                    label=""
                    options={allBranches
                      .filter((b) => formData.business_ids.includes(b.business_id))
                      .map((b) => ({ 
                        id: b.loyverse_store_id, 
                        name: `${b.name} (${b.business_name})` 
                      }))}
                    selected={formData.branch_ids}
                    onChange={(ids) => setFormData({ ...formData, branch_ids: ids })}
                    testId="user-branches-select"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground italic">Loading branches...</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Leave empty for access to all branches in selected businesses
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Welcome Email Confirmation */}
      <AlertDialog open={welcomeDialogOpen} onOpenChange={setWelcomeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Welcome Email</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will send a welcome email to <strong>{welcomeUser?.email}</strong> with a link to set their password.
              </p>
              <p className="text-amber-600 font-medium">
                Warning: This will reset the user's current password. They will need to use the link in the email or the temporary password to log in.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={sendWelcomeEmail}
              data-testid="confirm-send-welcome"
            >
              Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
